import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

interface AllowedModule {
  module_id: string;
  module_code: string;
  module_name: string;
  module_route: string;
  module_icon: string;
  parent_id: string | null;
  sort_order: number;
}

export const useModulePermissions = () => {
  const { user } = useAuth();
  const [allowedModules, setAllowedModules] = useState<AllowedModule[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAllowedModules = useCallback(async () => {
    if (!user) {
      setAllowedModules([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_user_allowed_modules', {
        _user_id: user.id
      });

      if (error) throw error;
      setAllowedModules(data || []);
    } catch (error) {
      console.error('Error loading allowed modules:', error);
      setAllowedModules([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadAllowedModules();
  }, [loadAllowedModules]);

  const canViewModule = useCallback((moduleCode: string): boolean => {
    return allowedModules.some(module => module.module_code === moduleCode);
  }, [allowedModules]);

  const getModuleByCode = useCallback((moduleCode: string): AllowedModule | undefined => {
    return allowedModules.find(module => module.module_code === moduleCode);
  }, [allowedModules]);

  return {
    allowedModules,
    loading,
    canViewModule,
    getModuleByCode,
    refetch: loadAllowedModules
  };
};